export type DateFormat = 'short' | 'medium' | 'long' | 'full';

const DATE_OPTIONS: Record<DateFormat, Intl.DateTimeFormatOptions> = {
  short: { day: '2-digit', month: '2-digit', year: '2-digit' },
  medium: { day: '2-digit', month: 'short', year: 'numeric' },
  long: { day: '2-digit', month: 'long', year: 'numeric' },
  full: { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' },
};

const DATETIME_OPTIONS: Record<DateFormat, Intl.DateTimeFormatOptions> = {
  short: {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  },
  medium: {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
  long: {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
  full: {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
};

/**
 * Formats a date value to a localized date string
 * @param date - Date string, Date object, null, or undefined
 * @param format - Output format: 'short', 'medium', 'long', or 'full'
 * @param locale - Locale for formatting (default: 'es-MX')
 * @param timeZone - IANA timezone (e.g. 'America/Mexico_City')
 * @returns Formatted date string or em-dash for null/undefined
 */
export function formatDate(
  date: string | Date | null | undefined,
  format: DateFormat = 'medium',
  locale = 'es-MX',
  timeZone?: string,
): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString(locale, {
    ...DATE_OPTIONS[format],
    ...(timeZone && { timeZone }),
  });
}

/**
 * Formats a date value to a localized date-time string
 * @param date - Date string, Date object, null, or undefined
 * @param format - Output format: 'short', 'medium', 'long', or 'full'
 * @param locale - Locale for formatting (default: 'es-MX')
 * @param timeZone - IANA timezone (e.g. 'America/Mexico_City')
 * @returns Formatted date-time string or em-dash for null/undefined
 */
export function formatDateTime(
  date: string | Date | null | undefined,
  format: DateFormat = 'medium',
  locale = 'es-MX',
  timeZone?: string,
): string {
  if (!date) return '—';
  return new Date(date).toLocaleString(locale, {
    ...DATETIME_OPTIONS[format],
    ...(timeZone && { timeZone }),
  });
}

/**
 * Formats a date value to a UTC SQL datetime string (Y-m-d H:i:s).
 * Use when an API validator (e.g. Laravel `date_format:Y-m-d H:i:s`) requires
 * the canonical SQL shape but expects UTC values.
 * @param date - Date string (incl. `datetime-local` value), Date object, or millis
 * @returns Formatted UTC datetime string, e.g. `2026-05-21 03:00:00`
 */
export function toUtcSqlDateTime(date: string | Date | number): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) {
    throw new TypeError(
      `toUtcSqlDateTime: invalid date input "${String(date)}"`,
    );
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
  );
}

/**
 * Converts an ISO/date value to the local `datetime-local` input shape
 * (`YYYY-MM-DDTHH:mm`). Inverse of {@link toUtcSqlDateTime} for round-tripping
 * a stored UTC value back into an `<input type="datetime-local">`.
 * @param iso - Date string, null, or undefined
 * @returns Local datetime string, or empty string for null/undefined
 */
export function toDateTimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

const WALL_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const WALL_TIME_WITH_SECONDS_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;

const pad2 = (n: number) => String(n).padStart(2, '0');

type WallClockParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function wallClockPartsInZone(
  instant: number,
  timeZone: string,
): WallClockParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);

  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  };
}

function zoneOffsetMs(instant: number, timeZone: string): number {
  const p = wallClockPartsInZone(instant, timeZone);
  const wholeSeconds = Math.floor(instant / 1000) * 1000;
  return (
    Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) -
    wholeSeconds
  );
}

/**
 * Returns the IANA timezone the browser (or runtime) resolves dates in.
 * @returns IANA timezone, e.g. `America/Mexico_City`
 */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Tells whether the runtime knows `timeZone` as an IANA zone. `Intl` throws a
 * `RangeError` on any other string, so free-text zones must pass this first.
 * @param timeZone - Candidate zone, e.g. `America/Mazatlan`
 * @returns `true` when `Intl` accepts it
 */
export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Converts a `datetime-local` wall time, read as a clock in `timeZone`, to the
 * UTC instant it names. Unlike {@link toUtcSqlDateTime}, the zone is explicit,
 * so the result does not depend on where the browser runs.
 *
 * Two DST edge cases, neither of which reaches Mexico outside the US border
 * zones: a wall time the clocks skipped moves forward by the width of the gap
 * (02:30 reads back as 03:30 for a one-hour jump), and a wall time that happens
 * twice resolves to its earlier occurrence west of UTC, its later one east.
 * @param wallTime - `YYYY-MM-DDTHH:mm`
 * @param timeZone - IANA timezone (e.g. 'America/Mazatlan')
 * @returns UTC ISO string, e.g. `2026-09-17T06:59:00.000Z`
 */
export function wallTimeInZoneToUtcIso(
  wallTime: string,
  timeZone: string,
): string {
  const asUtc = parseWallTimeAsUtc(
    wallTime,
    WALL_TIME_PATTERN,
    'wallTimeInZoneToUtcIso',
  );
  return new Date(resolveWallClockInZone(asUtc, timeZone)).toISOString();
}

/**
 * Reads a wall time as if it were UTC, so its fields can be compared with the
 * zone's. Rejects a date the calendar would roll over (Feb 30); out-of-range
 * clock fields are left to the pattern.
 */
function parseWallTimeAsUtc(
  wallTime: string,
  pattern: RegExp,
  caller: string,
): number {
  const match = pattern.exec(wallTime);
  const [year, month, day, hour, minute, second = 0] = (
    match?.slice(1) ?? []
  ).map((part) => Number(part ?? 0));
  const asUtc = match
    ? Date.UTC(year, month - 1, day, hour, minute, second)
    : NaN;
  const normalized = new Date(asUtc);
  if (
    Number.isNaN(asUtc) ||
    normalized.getUTCMonth() !== month - 1 ||
    normalized.getUTCDate() !== day
  ) {
    throw new TypeError(`${caller}: invalid wall time "${wallTime}"`);
  }
  return asUtc;
}

function resolveWallClockInZone(asUtc: number, timeZone: string): number {
  const guessOffset = zoneOffsetMs(asUtc, timeZone);
  const guess = asUtc - guessOffset;
  const actualOffset = zoneOffsetMs(guess, timeZone);
  if (actualOffset === guessOffset) return guess;

  const corrected = asUtc - actualOffset;
  return zoneOffsetMs(corrected, timeZone) === actualOffset
    ? corrected
    : Math.max(guess, corrected);
}

/**
 * Writes a wall time, read as a clock in `timeZone`, as ISO 8601 with the
 * offset in force at that instant: `2026-10-10T23:59:59-06:00`. Unlike
 * {@link wallTimeInZoneToUtcIso}, the reader still sees the local clock and the
 * zone's offset, which is what an API that labels a period by its offset needs.
 *
 * The offset is per instant, so two wall times in a DST zone can carry two
 * offsets. DST edge cases resolve as in {@link wallTimeInZoneToUtcIso}, and the
 * clock written is the one the resolved instant shows.
 * @param wallTime - `YYYY-MM-DDTHH:mm` or `YYYY-MM-DDTHH:mm:ss`
 * @param timeZone - IANA timezone (e.g. 'America/Mazatlan')
 * @returns ISO string with offset, e.g. `2026-10-10T00:00:00-07:00`
 */
export function wallTimeInZoneToOffsetIso(
  wallTime: string,
  timeZone: string,
): string {
  const asUtc = parseWallTimeAsUtc(
    wallTime,
    WALL_TIME_WITH_SECONDS_PATTERN,
    'wallTimeInZoneToOffsetIso',
  );
  return instantToOffsetIso(resolveWallClockInZone(asUtc, timeZone), timeZone);
}

/**
 * Writes an instant as ISO 8601 with the wall clock it shows in `timeZone` and
 * the offset in force there: `2026-04-04T23:59:59-04:00`. Unlike a wall time,
 * an instant is never ambiguous, so it can name either side of a DST change.
 * @param instant - epoch milliseconds
 * @param timeZone - IANA timezone (e.g. 'America/Mazatlan')
 * @returns ISO string with offset, e.g. `2026-10-10T23:59:59-06:00`
 */
export function instantToOffsetIso(instant: number, timeZone: string): string {
  const p = wallClockPartsInZone(instant, timeZone);
  const offsetMinutes = Math.round(zoneOffsetMs(instant, timeZone) / 60000);
  const sign = offsetMinutes < 0 ? '-' : '+';
  const absMinutes = Math.abs(offsetMinutes);
  const offset = `${sign}${pad2(Math.floor(absMinutes / 60))}:${pad2(absMinutes % 60)}`;
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}T${pad2(p.hour)}:${pad2(p.minute)}:${pad2(p.second)}${offset}`;
}

/**
 * Converts a UTC instant to the `datetime-local` wall time it shows in
 * `timeZone`. Inverse of {@link wallTimeInZoneToUtcIso}; seconds are dropped,
 * as the input shape has none.
 * @param iso - ISO/date string, null, or undefined
 * @param timeZone - IANA timezone (e.g. 'America/Mazatlan')
 * @returns `YYYY-MM-DDTHH:mm`, or empty string for null/undefined
 */
export function utcToWallTimeInZone(
  iso: string | null | undefined,
  timeZone: string,
): string {
  if (!iso) return '';
  const instant = new Date(iso).getTime();
  if (Number.isNaN(instant)) {
    throw new TypeError(`utcToWallTimeInZone: invalid date input "${iso}"`);
  }
  const p = wallClockPartsInZone(instant, timeZone);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}T${pad2(p.hour)}:${pad2(p.minute)}`;
}

/**
 * Labels a timezone with its UTC offset at a given moment, e.g.
 * `America/Mazatlan (UTC−7)`. The offset is taken at `at` because zones that
 * observe DST change it through the year. With `until`, the label covers a
 * span: a span across a DST change names both offsets,
 * `America/Tijuana (UTC−8 → UTC−7)`, and one that keeps its offset names it once.
 * @param timeZone - IANA timezone
 * @param at - Moment the offset applies to, or the start of the span (default: now)
 * @param until - End of the span, when the label describes one
 * @returns Zone name with its offset(s), or just `UTC` for UTC itself
 */
export function formatTimeZoneLabel(
  timeZone: string,
  at: string | Date | number = Date.now(),
  until?: string | Date | number,
): string {
  const from = utcOffsetName(timeZone, at);
  const to = until === undefined ? from : utcOffsetName(timeZone, until);
  const offset = from === to ? from : `${from} → ${to}`;
  return offset === timeZone ? timeZone : `${timeZone} (${offset})`;
}

function utcOffsetName(timeZone: string, at: string | Date | number): string {
  const offsetName =
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(new Date(at))
      .find((part) => part.type === 'timeZoneName')?.value ?? 'GMT';
  return offsetName
    .replace(/^GMT([+-]0)?$/, 'GMT')
    .replace('GMT', 'UTC')
    .replace('-', '−');
}

/**
 * Formats a date value to a localized time string
 * @param date - Date string, Date object, null, or undefined
 * @param locale - Locale for formatting (default: 'es-MX')
 * @param timeZone - IANA timezone (e.g. 'America/Mexico_City')
 * @returns Formatted time string or em-dash for null/undefined
 */
export function formatTime(
  date: string | Date | null | undefined,
  locale = 'es-MX',
  timeZone?: string,
): string {
  if (!date) return '—';
  return new Date(date).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone && { timeZone }),
  });
}
